import { useEffect, useState } from "react";
import { ColumnsType } from "antd/es/table";
import { cellWidth } from "../../../core/methods/tableCellWidth";
import Datatable from "../../../core/common/dataTable";
import { useNavigate } from "react-router";
import { all_routes } from "../../router/all_routes";
import FiltersTrackCase, {
  FiltersSelectType,
  SearchFormDataType,
  SearchPayloadType,
} from "../FiltersTrackCase";
import TimelineModal from "../TimelineModal";
import { dummyData_Insufficiency } from "../../../core/data/js/dummy-data";
import ModifyInviteModal from "../applicant-invited/ModifyInviteModal";
import { getSearchableHeader } from "../../utils/getSearchableHeader";
import dayjs from "dayjs";
import CaseActionModal from "../work-in-progress/CaseActionModal";
import Swal from "sweetalert2";
import useApi from "../../../core/customhook/useApi";
import webservice_url from "../../../core/webservice/Webservice_url";
import SnipperLoader from "../../../core/common/loader/SnipperLoader";
import { exportAntdTableToExcel } from "../../utils/exportExcel";
import { parseDaysString } from "../../../core/methods/sorterMethods";

export type TableApiPayload = {
  program_id: string[];
  department_id: string[];
  status: string[];
  assigned_user_id: string[];
  project_id: string[];
  page: number;
  limit: number;
};
const buildTableApiPayload = (
  searchPayload: Partial<SearchPayloadType>,
  page: number,
  limit: number,
  statusId?: string,
): TableApiPayload => ({
  program_id: searchPayload.program ?? [],
  department_id: searchPayload.department ?? [],
  status: statusId ? [statusId] : [],
  project_id: searchPayload.project ?? [],
  assigned_user_id: searchPayload.users ?? [],
  page,
  limit,
});

interface insufficiencyProps {
  statusId?: string;
}
const Insufficiency = ({ statusId }: insufficiencyProps) => {
  const navigate = useNavigate();
  const [itemList, setItemList] = useState(dummyData_Insufficiency);
  const [total, setTotal] = useState(0);
  const [searchFilter, setSearchFilter] = useState<SearchFormDataType>({
    case_id: "",
    applicant_name: "",
    email: "",
    from_date: "",
    to_date: "",
  });
  const [filterData, setFilterData] = useState<FiltersSelectType>({
    organization: [],
    project: [],
    location: [],
    program: [],
    users: [],
  });
  const [stopLoading, setStopLoading] = useState(false);
  const [stopModalRecord, setStopModalRecord] = useState<any>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });

  const [showTimeline, setShowTimeline] = useState<boolean>(false);
  const [showModifyInvite, setShowModifyInvite] = useState<boolean>(false);
  const [lastSearchPayload, setLastSearchPayload] = useState<
    Partial<SearchPayloadType>
  >({});
  const [timelineData, setTimelineData] = useState<any[]>([]);
  const [timelineLoading, setTimelineLoading] = useState<boolean>(false);

  const [inventoryColumnSearch, setInventoryColumnSearch] = useState<
    Record<string, string>
  >({});

  type InviteItem = (typeof dummyData_Insufficiency)[number];
  const filteredData = itemList.filter((item: InviteItem) => {
    return Object.entries(inventoryColumnSearch).every(([key, value]) => {
      if (!value) return true;

      return String(item[key as keyof InviteItem] ?? "")
        .toLowerCase()
        .includes(value.toLowerCase());
    });
  });

  const getCaseLog = useApi<any>(webservice_url.ViewLogAPI, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const handleViewLog = async (caseId?: string) => {
    if (!caseId) return;
    setShowTimeline(true);
    setTimelineLoading(true);
    try {
      const res = await getCaseLog.callApi({ case_id: caseId });
      setTimelineData(res?.data ?? []);
    } catch (err) {
      console.error(err);
      setTimelineData([]);
    } finally {
      setTimelineLoading(false);
    }
  };
  // ---- columns ----
  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "serial_number",
      dataIndex: "serial_number",
      width: 80,
      align: "center",
    },
    {
      title: getSearchableHeader(
        "Applicant Name",
        "applicant_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "applicant_name",
      onCell: () => cellWidth(200),
      render: (text, record: any) => (
        <div
          className="wip-link-cell"
          onClick={() => {
            navigate(all_routes.caseDetails, {
              state: {
                case_id: record.case_id,
                assignment_id: record.assignment_id,
              },
            });
          }}
        >
          {text}
        </div>
      ),
      sorter: (a, b) => a.applicant_name.localeCompare(b.applicant_name),
    },
    {
      title: getSearchableHeader(
        "Case ID",
        "case_id",
        setInventoryColumnSearch,
      ),
      dataIndex: "case_id",
      onCell: () => cellWidth(200),
      sorter: (a, b) => a.case_id.localeCompare(b.case_id),
    },
    {
      title: getSearchableHeader(
        "Case SPOC",
        "case_spoc_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "case_spoc_name",
      onCell: () => cellWidth(200),
      sorter: (a, b) => a.case_spoc_name.localeCompare(b.case_spoc_name),
    },
    {
      title: getSearchableHeader(
        "Case Status",
        "overall_status",
        setInventoryColumnSearch,
      ),
      dataIndex: "overall_status",
      key: "overall_status",
      onCell: () => cellWidth(200),
      sorter: (a: any, b: any) =>
        (a.overall_status ?? "").localeCompare(b.overall_status ?? ""),
      render: (text: any, record: any) => {
        return (
          <span
            style={{
              backgroundColor: record?.overall_color_value || "#6c757d",
              color: "#fff",
              padding: "5px 10px",
              borderRadius: "4px",
              display: "inline-block",
              fontSize: "12px",
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            {text ?? "-"}
          </span>
        );
      },
    },
    {
      title: getSearchableHeader(
        "Check Name",
        "check_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "check_name",
      onCell: () => cellWidth(200),
      render: (text: string, record: any) => <div>{text}</div>,
      sorter: (a, b) => a.check_name.localeCompare(b.check_name),
    },
    {
      title: getSearchableHeader(
        "Project",
        "project_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "project_name",
      key: "project_name",
      onCell: () => cellWidth(200),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.project_name ?? "").localeCompare(b.project_name ?? ""),
    },
    {
      title: getSearchableHeader(
        "Program",
        "program_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "program_name",
      key: "program_name",
      onCell: () => cellWidth(200),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.program_name ?? "").localeCompare(b.program_name ?? ""),
    },
    {
      title: getSearchableHeader(
        "Department",
        "department_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "department_name",
      key: "department_name",
      onCell: () => cellWidth(200),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.department_name ?? "").localeCompare(b.department_name ?? ""),
    },
    {
      title: getSearchableHeader("Email Id", "email", setInventoryColumnSearch),
      dataIndex: "email",
      onCell: () => cellWidth(200),
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: getSearchableHeader(
        "Raise Date",
        "raise_date",
        setInventoryColumnSearch,
      ),
      dataIndex: "raise_date",
      onCell: () => cellWidth(200),
      render: (value: any) =>
        value ? new Date(value).toLocaleDateString() : "-",
      sorter: (a, b) =>
        new Date(a.raise_date).getTime() - new Date(b.raise_date).getTime(),
    },
    {
      title: getSearchableHeader(
        "Days Elapsed",
        "days_elapsed",
        setInventoryColumnSearch,
      ),
      dataIndex: "days_elapsed",
      key: "days_elapsed",
      onCell: () => cellWidth(200),
      render: (value: string | number) =>
        value !== null && value !== undefined && value !== ""
          ? Number(value).toFixed(2)
          : "-",
      sorter: (a: any, b: any) =>
        parseDaysString(a.days_elapsed) - parseDaysString(b.days_elapsed),
    },
    {
      title: "View Log",
      key: "viewLog",
      onCell: () => cellWidth(120),
      render: (_: any, record: any) => (
        <div
          className="text-center text-dark"
          style={{ cursor: "pointer" }}
          onClick={() => handleViewLog(record.case_id)}
        >
          <i className="ti ti-eye"></i>
        </div>
      ),
    },
  ];

  const getTableData = useApi<any>(webservice_url.getInsufficiencyReportAPI, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const getCaseStatuses = useApi<any>(webservice_url.GetCaseStatus, {
    method: "GET",
    autoCall: true,
    handleGlobalError: false,
  });
  const caseStatusUpdateAPI = useApi<any>(webservice_url.updateCaseStatus, {
    method: "PATCH",
    autoCall: false,
    handleGlobalError: false,
  });
  const token = localStorage.getItem("userId");

  const fetchTableData = async (
    searchPayload: Partial<SearchPayloadType>,
    page: number,
    limit: number,
  ) => {
    try {
      const apiPayload = buildTableApiPayload(
        searchPayload,
        page,
        limit,
        statusId,
      );
      const res = await getTableData.callApi(apiPayload);
      setItemList(res?.data?.table);
      const rows = res?.data?.table ?? [];
      setItemList(
        rows.map((item: any) => ({
          ...item,
          key: item.assignment_id,
        })),
      );
      const paginationInfo = res?.data?.pagination;
      setTotal(paginationInfo?.total);
      setPagination({
        page: paginationInfo?.page ?? page,
        limit: paginationInfo?.limit ?? limit,
      });
    } catch (err) {
      console.error(err);
      setItemList([]);
    }
  };
  useEffect(() => {
    fetchTableData({}, pagination.page, pagination.limit);
  }, [statusId]);
  const getStatusIdByName = (statusName: string): string | undefined => {
    const statusList = getCaseStatuses?.data?.data ?? [];
    return statusList.find((s: any) => s.status_name === statusName)?.status_id;
  };

  const handleConfirmStop = async (reason: string) => {
    if (!stopModalRecord) return;
    try {
      setStopLoading(true);

      const stopStatusId = getStatusIdByName("STOP");

      if (!stopStatusId) {
        Swal.fire({
          title: "Error",
          text: `Status "STOP" not found. Please refresh and try again.`,
          icon: "error",
        });
        return;
      }

      await caseStatusUpdateAPI.callApi({
        case_id: stopModalRecord?.case_id,
        status: stopStatusId,
        actor_id: token,
        actor_type: "admin",
        action_reason: reason,
      });

      setItemList((prev) =>
        prev.map((item: any) =>
          item.assignment_id === stopModalRecord.assignment_id
            ? { ...item, status: "STOP" }
            : item,
        ),
      );

      setStopModalRecord(null);

      await Swal.fire({
        title: "Stopped!",
        text: "The case has been stopped.",
        icon: "success",
        timer: 1500,
        showConfirmButton: true,
      });
    } catch (error) {
      console.error("Stop failed:", error);
      Swal.fire({
        title: "Error",
        text: "Something went wrong. Please try again.",
        icon: "error",
      });
    } finally {
      setStopLoading(false);
    }
  };
  const handleTableChange = (
    page: number,
    limit: number = pagination.limit,
  ) => {
    if (page === pagination.page && limit === pagination.limit) {
      return;
    }
    setPagination({ page, limit });
    fetchTableData(lastSearchPayload, page, limit);
  };

  const handleSearch = (payload: SearchPayloadType) => {
    setLastSearchPayload(payload);
    setPagination({ page: 1, limit: pagination.limit });
    fetchTableData(payload, 1, pagination.limit);
  };
  const handleDownloadExcel = () => {
    exportAntdTableToExcel({
      columns,
      dataSource: filteredData,
      fileName: "Insufficiency_Cases_Report",
      sheetName: "Insufficiency Cases",
    });
  };
  const loading = getTableData.loading;
  return (
    <>
      <FiltersTrackCase
        filterData={filterData}
        searchFilter={searchFilter}
        setFilterData={setFilterData}
        setSearchFilter={setSearchFilter}
        storageKey="insufficiency_cases"
        onSearch={handleSearch}
      />

      {/* Table  */}
      <div className="card mt-3 pt-2 pe-0">
        <div className="text-end px-2">
          <button
            className="btn btn-sm btn-primary"
            onClick={handleDownloadExcel}
            disabled={loading}
          >
            <i className="bi bi-download pe-2"></i>
           Export to Excel 
          </button>
        </div>
        {loading ? (
          <SnipperLoader />
        ) : (
          <Datatable
            rowKey="sno"
            dataSource={filteredData}
            columns={columns}
            fixedHeader
            tableHeight={650}
            pagination={{
              current: pagination.page,
              pageSize: pagination.limit,
              total,
            }}
            handleTableChange={(pagination: any) => {
              handleTableChange(pagination.current, pagination.pageSize);
            }}
          />
        )}
      </div>

      {/* Timeline */}
      {showTimeline && (
        <TimelineModal
          timelineData={timelineData}
          loading={timelineLoading}
          onClose={() => {
            setShowTimeline(false);
            setTimelineData([]);
          }}
        />
      )}
      {/* Modify Invite Modal */}
      {showModifyInvite && (
        <ModifyInviteModal
          handleClose={() => setShowModifyInvite(false)}
          handleSubmit={(d) => {
            setShowModifyInvite(false);
          }}
        />
      )}
      {stopModalRecord && (
        <CaseActionModal
          isOpen={!!stopModalRecord}
          title="Clear Insuffeciency"
          confirmLabel="Clear Insuffeciency"
          confirmButtonClass="btn-secondary"
          reasonLabel="Enter reason of clear insuff"
          onClose={() => setStopModalRecord(null)}
          onConfirm={handleConfirmStop}
          baseFields={[
            { label: "Case ID", value: stopModalRecord.case_id },
            { label: "Candidate Name", value: stopModalRecord.applicant_name },
            {
              label: "Case Receive Date",
              value: dayjs(stopModalRecord.case_receive_date).format(
                "DD-MM-YYYY",
              ),
            },
            {
              label: "SLA Start Date",
              value: dayjs(stopModalRecord.sla_start_date).format("DD-MM-YYYY"),
            },
            {
              label: "Due Date",
              value: dayjs(stopModalRecord.due_date).format("DD-MM-YYYY"),
            },
          ]}
          extraFields={
            [
              // { label: "Stop Reason", value: stopModalRecord.action },
            ]
          }
          loading={stopLoading}
        />
      )}
    </>
  );
};

export default Insufficiency;
